import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import './ForceChangePassword.css';

function defaultRoute(role) {
  if (role === 'SuperAdmin') return '/superadmin';
  if (role === 'Admin')      return '/admin';
  return '/dashboard';
}

export default function ForceChangePassword() {
  const { actions, role } = useApp();
  const navigate = useNavigate();

  // Auth guard removed -- user is not authenticated yet during the Cognito
  // FORCE_CHANGE_PASSWORD challenge. Tokens are only issued after
  // confirmSignIn succeeds, which is what this page does.
  const [form,    setForm]    = useState({ next: '', confirm: '' });
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const errs = {};

    const password = form.next;

    if (password.length < 8) {
      errs.next = "Password must be at least 8 characters long.";
      return errs;
    }

    if (!/[A-Z]/.test(password)) {
      errs.next = "Password must contain at least one uppercase letter.";
      return errs;
    }

    if (!/[a-z]/.test(password)) {
      errs.next = "Password must contain at least one lowercase letter.";
      return errs;
    }

    if (!/[0-9]/.test(password)) {
      errs.next = "Password must contain at least one number.";
      return errs;
    }

    if (!/[!@#$%^&*()_\-+=[\]{};':"\\|,.<>/?`~]/.test(password)) {
      errs.next = "Password must contain at least one special character.";
      return errs;
    }

    if (form.next !== form.confirm) {
      errs.confirm = "Passwords do not match.";
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      // confirmSignIn completes the Cognito challenge with the new password.
      // After this, Cognito issues real tokens and the user is fully logged in.
      await actions.changePasswordChallenge(form.next);
      navigate(defaultRoute(role), { replace: true });
    } catch (err) {
      setErrors({ general: err.message || 'Failed to set password. Please try logging in again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setForm((p) => ({ ...p, [field]: e.target.value }));
    setErrors((p) => ({ ...p, [field]: '', general: '' }));
  };

  return (
    <div className="fcp-root">
      <div className="fcp-card animate-in">
        <div className="fcp-icon">🔐</div>
        <h1 className="fcp-title">Set your password</h1>
        <p className="fcp-desc">
          Your account was created with a temporary password.
          Please set a new password before continuing.
        </p>

        {errors.general && <div className="error-banner">{errors.general}</div>}

        <form onSubmit={handleSubmit} noValidate>
          {[
            { label: 'New password',         field: 'next',    placeholder: '8+ chars, uppercase, lowercase, number & symbol' },
            { label: 'Confirm new password',  field: 'confirm', placeholder: 'Repeat new password'   },
          ].map(({ label, field, placeholder }) => (
            <div key={field} className={`field-group ${errors[field] ? 'has-error' : ''}`}>
              <label>{label}</label>
              <input
                type="password"
                className="field-input"
                placeholder={placeholder}
                value={form[field]}
                onChange={handleChange(field)}
                autoComplete="new-password"
              />
              {errors[field] && <span className="field-error">{errors[field]}</span>}

              {field === "next" && (
                <small className="password-hint">
                  Must contain at least 8 characters, one uppercase letter, one lowercase
                  letter, one number and one special character.
                </small>
              )}
            </div>
          ))}

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? <><span className="spinner" /> Updating…</> : 'Set new password →'}
          </button>
        </form>
      </div>
    </div>
  );
}